'use client'

import { Check, ChevronDown, Monitor, Moon, Sun } from 'lucide-react'
import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

import { RADIUS, SURFACE_SOLID } from '@/lib/surface'
import {
  applyTheme,
  readThemeChoice,
  storeThemeChoice,
  THEME_CHOICES,
  THEME_LABELS,
  type ThemeChoice,
} from '@/lib/theme'
import { MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Appearance switcher — the language picker's twin, and deliberately so.
 *
 * Two controls that do the same KIND of thing (pick one of a short list, remember
 * it, live in the footer) should not be two different interactions. This shares
 * the language dropdown's shape down to the upward-opening menu, the arrow
 * navigation and the tick on the current row, so learning one teaches the other.
 *
 * Three rows rather than a toggle. A two-state switch cannot express "follow the
 * system", which is the setting a large share of people actually want and the
 * only one that keeps up when their phone flips at sunset.
 */

const ICONS: Record<ThemeChoice, ReactElement> = {
  system: <Monitor size={14} aria-hidden="true" />,
  light: <Sun size={14} aria-hidden="true" />,
  dark: <Moon size={14} aria-hidden="true" />,
}

export function ThemeDropdown({ triggerClassName }: { triggerClassName?: string } = {}) {
  const [open, setOpen] = useState(false)
  /**
   * Starts at the DEFAULT rather than at the stored value, and is corrected in
   * an effect. The server cannot read localStorage, so rendering the stored
   * choice directly would be a hydration mismatch on every visitor who has
   * chosen anything — the document is already correct by then, because the boot
   * script stamped it before paint; only this label needs catching up.
   */
  const [choice, setChoice] = useState<ThemeChoice>('dark')
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => setChoice(readThemeChoice()), [])

  // Only while "system" is selected: the OS can flip under us — at sunset, or
  // because somebody changed a setting in another window — and the page has to
  // follow without a reload. Any explicit choice ignores the OS entirely.
  useEffect(() => {
    if (choice !== 'system') return
    const query = window.matchMedia('(prefers-color-scheme: light)')
    const sync = () => applyTheme('system')
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [choice])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Focus returns to the trigger, not to wherever the document left it.
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // A `role="menu"` is arrow-navigated rather than tab-navigated, so opening it
  // has to place focus inside — on the option already in use.
  useEffect(() => {
    if (!open) return
    const index = Math.max(0, THEME_CHOICES.indexOf(choice))
    const frame = requestAnimationFrame(() => itemRefs.current[index]?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open, choice])

  const focusItem = (index: number) => {
    const count = THEME_CHOICES.length
    itemRefs.current[((index % count) + count) % count]?.focus()
  }

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const active = itemRefs.current.findIndex((element) => element === document.activeElement)
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusItem(active + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusItem(active - 1)
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  const pick = (next: ThemeChoice) => {
    setChoice(next)
    // Applied FIRST, stored second: the visitor sees the change immediately and
    // a storage failure cannot swallow it.
    applyTheme(next)
    storeThemeChoice(next)
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Wygląd: ${THEME_LABELS[choice]}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown') return
          event.preventDefault()
          setOpen(true)
        }}
        className={
          triggerClassName ??
          cn(
            'inline-flex min-h-11 items-center gap-2 border border-foreground/15 px-3 text-foreground/70',
            'transition-colors hover:border-foreground/30 hover:text-foreground',
            'focus-visible:ring-2 focus-visible:ring-foreground/50 focus-visible:outline-none',
            RADIUS.pill,
          )
        }
      >
        {ICONS[choice]}
        <span
          className={
            triggerClassName ? undefined : cn(MICRO, 'font-semibold tracking-[0.12em] uppercase')
          }
        >
          Wygląd
        </span>
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={cn('transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Wygląd"
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute right-0 bottom-full z-50 mb-2 min-w-48 p-1 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.6)]',
            RADIUS.box,
            SURFACE_SOLID,
          )}
        >
          {THEME_CHOICES.map((option, index) => {
            const active = option === choice
            return (
              <button
                key={option}
                ref={(element) => {
                  itemRefs.current[index] = element
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-current={active ? 'true' : undefined}
                onClick={() => pick(option)}
                className={cn(
                  'flex w-full items-center justify-between gap-8 rounded-lg px-3 py-2.5 text-sm outline-none',
                  'transition-colors hover:bg-foreground/[0.07] focus-visible:bg-foreground/[0.07]',
                  active ? 'text-foreground' : 'text-foreground/70 hover:text-foreground',
                )}
              >
                <span className="flex items-center gap-2.5">
                  {ICONS[option]}
                  {THEME_LABELS[option]}
                </span>
                {active ? <Check size={14} aria-hidden="true" /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
